import React, { useEffect, useState } from "react";
import { supabase } from "../utils/supabaseClient";
import "../styles/AdminDashboard.css";

function AdminDashboard() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editingQuoteId, setEditingQuoteId] = useState(null);
  const [editQuoteData, setEditQuoteData] = useState({
    message: "",
    status: "",
    items: [],
  });

  // Fetch quotes including their items
  const fetchQuotes = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("quotes")
      .select("*, quote_items(*)")
      .order("created_at", { ascending: false });
    if (error) console.error(error);
    else setQuotes(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchQuotes();
  }, []);

  // Delete quote + items
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this quote?")) return;
    await supabase.from("quote_items").delete().eq("quote_id", id);
    await supabase.from("quotes").delete().eq("id", id);
    fetchQuotes();
  };

  // Send quotation email via default email client
  const handleSendEmail = async (quote) => {
    try {
      const subject = encodeURIComponent("Your Quotation");
      const body = encodeURIComponent(`
Hello ${quote.customer_name},

Here is your quotation:

${quote.quote_items
  .map(
    (item) =>
      `${item.service_name} - Hours: ${item.hours}, Rate: ${item.rate_per_hour}, Cost: ${item.cost}`
  )
  .join("\n")}

Total Cost: ${quote.total_cost}

Message: ${quote.message}
Phone: ${quote.customer_phone}
      `);

      // Open email client
      window.location.href = `mailto:${quote.customer_email}?subject=${subject}&body=${body}`;

      // Update quote status in Supabase
      await supabase.from("quotes").update({ status: "sent" }).eq("id", quote.id);
      fetchQuotes();
    } catch (err) {
      console.error(err);
      alert("Failed to open email client.");
    }
  };

  // Start editing a quote
  const handleEditQuote = (quote) => {
    setEditingQuoteId(quote.id);
    setEditQuoteData({
      message: quote.message,
      status: quote.status,
      items: quote.quote_items.map((item) => ({ ...item })),
    });
  };

  // Update service item fields (hours, rate)
  const handleItemChange = (index, field, value) => {
    setEditQuoteData((prev) => {
      const updatedItems = [...prev.items];
      updatedItems[index][field] = value;
      updatedItems[index].cost =
        Number(updatedItems[index].hours) * Number(updatedItems[index].rate_per_hour);
      return { ...prev, items: updatedItems };
    });
  };

  // Save edited quote and items
  const handleSaveEdit = async (quoteId) => {
    for (const item of editQuoteData.items) {
      await supabase
        .from("quote_items")
        .update({
          hours: item.hours,
          rate_per_hour: item.rate_per_hour,
          cost: item.cost,
        })
        .eq("id", item.id);
    }

    const totalCost = editQuoteData.items.reduce((sum, i) => sum + Number(i.cost), 0);

    await supabase
      .from("quotes")
      .update({
        message: editQuoteData.message,
        status: editQuoteData.status,
        total_cost: totalCost,
      })
      .eq("id", quoteId);

    setEditingQuoteId(null);
    fetchQuotes();
  };

  // Filter quotes
  const filteredQuotes = quotes.filter((q) => {
    const matchesSearch =
      q.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.customer_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.customer_phone.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.quote_items.some((item) =>
        item.service_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    const matchesStatus = statusFilter === "all" ? true : q.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalCost = filteredQuotes.reduce((sum, q) => sum + Number(q.total_cost), 0);

  return (
    <div className="admin-dashboard">
      <h2>Admin Dashboard</h2>

      <div className="dashboard-controls">
        <input
          type="text"
          placeholder="Search by customer, phone, or service..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="sent">Sent</option>
          <option value="completed">Completed</option>
        </select>
      </div>

      <p>
        <strong>Total Quotes:</strong> {filteredQuotes.length} |{" "}
        <strong>Total Charges:</strong> R{totalCost.toFixed(2)}
      </p>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="cards-container">
          {filteredQuotes.map((q) => (
            <div key={q.id} className={`quote-card ${q.status}`}>
              <div className="card-header">
                <h3>
                  {q.customer_name} ({q.customer_email}) - {q.customer_phone}
                </h3>
                <span className={`status ${q.status}`}>{q.status.toUpperCase()}</span>
              </div>

              <div className="card-body">
                {editingQuoteId === q.id ? (
                  <>
                    <p><strong>Phone:</strong> {q.customer_phone}</p>
                    {editQuoteData.items.map((item, index) => (
                      <div key={item.id} className="quote-item">
                        <p><strong>{item.service_name}</strong></p>
                        <input
                          type="number"
                          value={item.hours}
                          onChange={(e) => handleItemChange(index, "hours", e.target.value)}
                          placeholder="Hours"
                        />
                        <input
                          type="number"
                          value={item.rate_per_hour}
                          onChange={(e) => handleItemChange(index, "rate_per_hour", e.target.value)}
                          placeholder="Rate per hour"
                        />
                        <p>Cost: R{item.cost.toFixed(2)}</p>
                      </div>
                    ))}
                    <textarea
                      name="message"
                      value={editQuoteData.message}
                      onChange={(e) =>
                        setEditQuoteData((prev) => ({ ...prev, message: e.target.value }))
                      }
                      placeholder="Message"
                    />
                    <select
                      name="status"
                      value={editQuoteData.status}
                      onChange={(e) =>
                        setEditQuoteData((prev) => ({ ...prev, status: e.target.value }))
                      }
                    >
                      <option value="pending">Pending</option>
                      <option value="sent">Sent</option>
                      <option value="completed">Completed</option>
                    </select>
                    <p>
                      <strong>Total Quote Cost:</strong>{" "}
                      {editQuoteData.items.reduce((sum, i) => sum + Number(i.cost), 0).toFixed(2)}
                    </p>
                  </>
                ) : (
                  <>
                    {q.quote_items.map((item) => (
                      <div key={item.id} className="quote-item">
                        <p><strong>{item.service_name}</strong></p>
                        <p>Hours: {item.hours}</p>
                        <p>Rate: R{item.rate_per_hour}</p>
                        <p>Cost: R{item.cost}</p>
                      </div>
                    ))}
                    <p><strong>Message:</strong> {q.message}</p>
                    <p><strong>Phone:</strong> {q.customer_phone}</p>
                    <p><strong>Total Quote Cost:</strong> R{q.total_cost}</p>
                  </>
                )}
              </div>

              <div className="card-actions">
                {editingQuoteId === q.id ? (
                  <>
                    <button className="save-btn" onClick={() => handleSaveEdit(q.id)}>Save</button>
                    <button className="delete-btn" onClick={() => setEditingQuoteId(null)}>Cancel</button>
                  </>
                ) : (
                  <>
                    {q.status === "pending" && (
                      <button className="send-btn" onClick={() => handleSendEmail(q)}>
                        Send Quotation
                      </button>
                    )}
                    <button className="edit-btn" onClick={() => handleEditQuote(q)}>Edit</button>
                    <button className="delete-btn" onClick={() => handleDelete(q.id)}>Delete</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
